import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  User,
  LogOut,
  X,
  Home,
  GraduationCap,
  BookOpen,
  Users,
  ChevronLeft,
  HelpCircle,
  LayoutDashboard,
} from 'lucide-react';
import { BRAND_NAME, BRAND_FULL_NAME, logoImage } from '../../config/brand';
import ThemeToggle from './ThemeToggle';

type NavLinkItem = {
  type: 'link';
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPrefix?: boolean;
};

type NavButtonItem = {
  type: 'button';
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  sectionId?: string;
};

type NavItem = NavLinkItem | NavButtonItem;

const NAV_EASE = 'duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser, hasEnrolledCourses, checkEnrolledCourses } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [mounted, setMounted] = useState(false);

  const isHomePage = location.pathname === '/';
  const isHeroOverlay = isHomePage && !isScrolled;
  const isRegisterPage = location.pathname === '/register';

  const isCurrentPath = (path: string, matchPrefix = false) =>
    matchPrefix ? location.pathname.startsWith(path) : location.pathname === path;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated && currentUser?.id && hasEnrolledCourses === null) {
      checkEnrolledCourses();
    }
  }, [isAuthenticated, currentUser?.id, hasEnrolledCourses, checkEnrolledCourses]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 48);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const section = document.getElementById(sectionId);
      if (section && location.pathname === '/') {
        section.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      if (location.pathname !== '/') {
        window.location.href = `/#${sectionId}`;
        return;
      }
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    },
    [location.pathname, navigate],
  );

  const guestNavItems: NavItem[] = [
    { type: 'link', to: '/', label: 'الرئيسية', icon: Home },
    {
      type: 'button',
      label: 'الكورسات',
      icon: BookOpen,
      sectionId: 'suggested-courses-section',
      onClick: () => scrollToSection('suggested-courses-section'),
    },
    {
      type: 'button',
      label: 'المدرسون',
      icon: Users,
      sectionId: 'teachers-section',
      onClick: () => scrollToSection('teachers-section'),
    },
    {
      type: 'button',
      label: 'الأسئلة الشائعة',
      icon: HelpCircle,
      sectionId: 'faq-section',
      onClick: () => scrollToSection('faq-section'),
    },
  ];

  const authNavItems: NavItem[] = [
    {
      type: 'link',
      to: '/student-portal',
      label: 'بوابة الطالب',
      icon: LayoutDashboard,
      matchPrefix: true,
    },
    ...(hasEnrolledCourses
      ? [{ type: 'link' as const, to: '/student-portal', label: 'كورساتي وحصصي', icon: GraduationCap }]
      : []),
    { type: 'link', to: '/profile', label: 'حسابي', icon: User },
  ];

  const navItems = mounted && isAuthenticated ? authNavItems : guestNavItems;

  const isItemActive = (item: NavItem) => {
    if (item.type === 'link') {
      return isCurrentPath(item.to, item.matchPrefix);
    }
    if (item.sectionId === 'suggested-courses-section' && location.pathname.startsWith('/courses')) {
      return true;
    }
    if (item.sectionId === 'teachers-section' && location.pathname.startsWith('/teacher')) {
      return true;
    }
    return false;
  };

  const headerPadClass = isHeroOverlay ? 'px-0' : 'px-2.5 sm:px-4';
  const innerShellClass = isHeroOverlay
    ? 'rounded-none border-transparent bg-transparent shadow-none translate-y-0'
    : 'translate-y-2 rounded-full border-gold-500/30 bg-green-600 shadow-[0_12px_32px_rgba(16,43,36,0.16),0_2px_8px_rgba(16,43,36,0.08)] sm:translate-y-3';
  const gridHeightClass = isHeroOverlay
    ? 'min-h-[4.75rem]'
    : 'min-h-[3.75rem] max-w-none px-[0.85rem] sm:min-h-[4.25rem] sm:px-6';

  const desktopLinkClass = (active: boolean) => {
    const base = `relative inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full px-3 font-thmanyah text-sm font-semibold transition-[color,background-color,border-color,box-shadow,opacity] ${NAV_EASE} focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2`;
    if (isHeroOverlay) {
      return `${base} ${active ? 'text-white' : 'text-white/85 hover:bg-white/10 hover:text-white'}`;
    }
    return `${base} border border-gold-500/40 bg-paper text-green-900 ${
      active ? '' : 'hover:border-gold-400 hover:bg-white'
    }`;
  };

  const mobileLinkClass = (active: boolean) =>
    `flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
      active
        ? 'bg-green-800 text-white shadow-md shadow-green-900/15'
        : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
    }`;

  const loginBtnClass = isHeroOverlay
    ? `border border-white/55 bg-white/10 text-white hover:border-white hover:bg-white/18`
    : `border border-white/35 bg-green-900/35 text-white hover:border-white/65 hover:bg-white/10`;

  const registerBtnClass = isHeroOverlay
    ? `border border-gold-400 bg-gold-500 text-green-950 hover:border-gold-300 hover:bg-gold-400`
    : `border border-gold-500/55 bg-paper text-green-900 hover:border-gold-500 hover:bg-white`;

  const actionBtnBase = `inline-flex min-h-12 items-center justify-center whitespace-nowrap rounded-[14px] px-[1.1rem] font-thmanyah text-sm font-semibold transition-[color,background-color,border-color,box-shadow,opacity] ${NAV_EASE} focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2`;

  const hamburgerClass = isHeroOverlay
    ? 'border border-white/45 bg-white/15 text-white'
    : 'border border-gold-500/45 bg-paper text-green-900';

  const themeToggleClass = isHeroOverlay
    ? 'border-white/45 bg-white/15 text-white hover:bg-white/25'
    : 'border-gold-500/45 bg-paper text-green-900 hover:bg-white';

  const renderNavItem = (item: NavItem, variant: 'desktop' | 'mobile', onNavigate?: () => void) => {
    const Icon = item.icon;
    const active = isItemActive(item);
    const className = variant === 'desktop' ? desktopLinkClass(active) : mobileLinkClass(active);

    if (item.type === 'link') {
      return (
        <Link key={item.to + item.label} to={item.to} className={className} onClick={onNavigate}>
          {variant === 'mobile' && <Icon className="h-5 w-5 shrink-0" />}
          {item.label}
          {variant === 'desktop' && isHeroOverlay && (
            <span
              className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full bg-gold-500 transition-opacity duration-500 ${
                active ? 'opacity-100' : 'opacity-0'
              }`}
              aria-hidden
            />
          )}
          {variant === 'mobile' && <ChevronLeft className="mr-auto h-4 w-4 opacity-40" />}
        </Link>
      );
    }

    return (
      <button
        key={item.label}
        type="button"
        onClick={() => {
          item.onClick();
          onNavigate?.();
        }}
        className={className}
      >
        {variant === 'mobile' && <Icon className="h-5 w-5 shrink-0" />}
        {item.label}
        {variant === 'desktop' && isHeroOverlay && (
          <span
            className={`absolute inset-x-[0.85rem] bottom-[0.35rem] h-0.5 rounded-full bg-gold-500 transition-opacity duration-500 ${
              active ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden
          />
        )}
        {variant === 'mobile' && <ChevronLeft className="mr-auto h-4 w-4 opacity-40" />}
      </button>
    );
  };

  return (
    <>
      <header
        className={`pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center transition-[padding,opacity] ${NAV_EASE} ${headerPadClass}`}
        dir="rtl"
      >
        <div
          className={`pointer-events-auto w-full max-w-[1360px] border transition-[background-color,border-color,border-radius,box-shadow,transform,opacity] ${NAV_EASE} ${innerShellClass}`}
        >
          <div
            className={`mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 transition-[min-height,padding] ${NAV_EASE} sm:px-6 lg:grid lg:grid-cols-[minmax(180px,1fr)_auto_minmax(180px,1fr)] lg:gap-4 lg:px-7 ${gridHeightClass}`}
          >
            <div className="order-1 justify-self-start lg:order-none">
              <Link
                to={mounted && isAuthenticated ? '/student-portal' : '/'}
                className="flex min-h-[44px] min-w-0 shrink-0 items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2"
                aria-label={`${BRAND_FULL_NAME} — الصفحة الرئيسية`}
              >
                {isHeroOverlay && (
                  <span className="me-[0.55rem] inline-block h-[1.35rem] w-[0.2rem] rounded-full bg-gold-500" aria-hidden />
                )}
                <span
                  className={`inline-flex items-center gap-2 rounded-2xl border px-[0.7rem] py-1 transition-[background-color,border-color,box-shadow] ${NAV_EASE} ${
                    isHeroOverlay
                      ? 'border-white/35 bg-paper/95 shadow-[0_4px_16px_rgba(16,43,36,0.18)]'
                      : 'border-gold-500/45 bg-paper'
                  }`}
                >
                  <img
                    src={logoImage}
                    alt={`شعار ${BRAND_NAME}`}
                    title={BRAND_NAME}
                    className={`logo-transparent w-auto object-contain object-center ${
                      isHeroOverlay
                        ? 'h-[clamp(2.05rem,3.2vw,2.55rem)]'
                        : 'h-[clamp(1.85rem,3vw,2.25rem)]'
                    }`}
                  />
                  <span className="hidden font-thmanyah text-sm font-bold leading-none text-green-800 sm:inline">
                    {BRAND_NAME}
                  </span>
                </span>
              </Link>
            </div>

            <nav
              className="order-3 hidden items-center justify-self-center gap-1 lg:order-none lg:flex"
              aria-label="التنقل الرئيسي"
            >
              {navItems.map((item) => renderNavItem(item, 'desktop'))}
            </nav>

            <div className="order-4 hidden items-center justify-end justify-self-end gap-2 lg:order-none lg:flex">
              <ThemeToggle className={themeToggleClass} />
              {mounted && isAuthenticated ? (
                <>
                  <div className="flex max-w-[140px] items-center gap-2 rounded-full border border-white/20 bg-white/10 px-2 py-1">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-700 to-gold-500">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <span className="truncate text-xs font-semibold text-white sm:text-sm">
                      {currentUser?.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={logout}
                    className={`${actionBtnBase} ${loginBtnClass}`}
                  >
                    <LogOut className="me-1.5 h-4 w-4" />
                    خروج
                  </button>
                  <Link to="/student-portal" className={`${actionBtnBase} ${registerBtnClass}`}>
                    البوابة
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className={`${actionBtnBase} ${loginBtnClass}`}>
                    تسجيل الدخول
                  </Link>
                  {!isRegisterPage && (
                    <Link to="/register" className={`${actionBtnBase} ${registerBtnClass}`}>
                      إنشاء حساب
                    </Link>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className={`order-3 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] lg:hidden ${NAV_EASE} focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 ${hamburgerClass}`}
              aria-label={isMobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <span aria-hidden className="flex flex-col gap-1.5">
                  <span className="block h-0.5 w-5 rounded-full bg-current" />
                  <span className="block h-0.5 w-5 rounded-full bg-current" />
                  <span className="block h-0.5 w-4 rounded-full bg-current" />
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" dir="rtl">
          <button
            type="button"
            className="absolute inset-0 bg-green-950/30 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="إغلاق القائمة"
          />
          <div className="absolute inset-x-3 top-16 max-h-[calc(100vh-5rem)] overflow-y-auto rounded-2xl border border-green-100 bg-white shadow-2xl shadow-green-900/10 sm:inset-x-4 sm:top-[4.5rem]">
            <div className="border-b border-green-100 px-4 py-3">
              <p className="text-xs font-semibold text-gold-700">القائمة</p>
              <p className="text-sm font-bold text-green-900">
                {mounted && isAuthenticated ? 'بوابة الطالب' : 'استكشف المنصة'}
              </p>
            </div>

            <div className="space-y-1 p-3">
              {navItems.map((item) => renderNavItem(item, 'mobile', () => setIsMobileMenuOpen(false)))}
            </div>

            <div className="space-y-2 border-t border-green-100 p-3">
              <ThemeToggle
                showLabel
                className="border border-green-200 bg-green-50 text-green-800 hover:border-gold-400 hover:bg-gold-50/50"
              />
              {mounted && isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 rounded-xl bg-green-50 px-4 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-green-700 to-gold-500">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-green-900">{currentUser?.name}</p>
                      <p className="text-xs text-gray-500">طالب مسجّل</p>
                    </div>
                  </div>
                  <Link
                    to="/student-portal"
                    className="flex min-h-11 items-center justify-center gap-2 rounded-[14px] bg-green-800 px-4 py-3 text-sm font-semibold text-white"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="h-5 w-5" />
                    بوابة الطالب
                  </Link>
                  <button
                    type="button"
                    onClick={async () => {
                      await logout();
                      setIsMobileMenuOpen(false);
                      navigate('/', { replace: true });
                    }}
                    className="flex min-h-11 w-full items-center justify-center gap-2 rounded-[14px] bg-red-50 px-4 py-3 text-sm font-semibold text-red-600"
                  >
                    <LogOut className="h-5 w-5" />
                    تسجيل الخروج
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex min-h-11 items-center justify-center rounded-[14px] border border-green-200 px-4 py-3 text-sm font-semibold text-green-800"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    تسجيل الدخول
                  </Link>
                  <Link
                    to="/register"
                    className="flex min-h-11 items-center justify-center rounded-[14px] bg-gold-500 px-4 py-3 text-sm font-semibold text-green-950"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    إنشاء حساب
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
